import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useSaveAsStyleExample from './useSaveAsStyleExample';
import './SaenggibuResultMoreMenu.css';

/**
 * 결과 카드 헤더의 '더보기(⋯)' 메뉴.
 * 자주 쓰는 동작(복사·AI로 다듬기)은 밖에 두고, 덜 쓰는 동작(직접 수정·비슷한 문장·예시로 저장 등)을 접는다.
 *
 * - 시각 크기는 헤더 한 줄에 맞춰 32px, 터치 영역은 ::after 로 44px 확보
 * - 선택 · 바깥 클릭 · Esc 로 닫힘, aria-haspopup / aria-expanded, 화살표 키 이동
 *
 * items: [{ key, label, onSelect, disabled?, tone?: 'danger' }] — falsy 항목은 무시 (조건부 노출용)
 * saveExample: useSaveAsStyleExample 인자. 넘기면 PRO·본문 있을 때 '예시로 저장' 항목을 items 뒤에 붙인다.
 */
const SaenggibuResultMoreMenu = ({
  items = [],
  saveExample = null,
  disabled = false,
  label = '더보기',
  size = 'compact', // 'compact' | 'pill'
  className = '',
  align = 'right',
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 8 });
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const example = useSaveAsStyleExample(saveExample || {});

  const visibleItems = (Array.isArray(items) ? items : []).filter(Boolean);
  if (saveExample && example.available) {
    const saveItem = {
      key: 'save_example',
      label: example.label,
      disabled: example.saving || Boolean(saveExample.disabled),
      onSelect: example.save,
    };
    // 위험 동작(삭제)은 항상 맨 아래 — 예시 저장은 그 앞에 넣는다
    const dangerIndex = visibleItems.findIndex((item) => item.tone === 'danger');
    if (dangerIndex >= 0) visibleItems.splice(dangerIndex, 0, saveItem);
    else visibleItems.push(saveItem);
  }

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus?.();
  }, []);

  // 결과 카드는 overflow:hidden 이라 카드 안에 그리면 메뉴가 잘린다 → body 포털 + fixed 좌표
  const updatePosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect?.();
    if (!rect) return;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    setPosition({
      top: rect.bottom + 6,
      ...(align === 'left'
        ? { left: Math.max(8, rect.left) }
        : { right: Math.max(8, viewportWidth - rect.right) }),
    });
  }, [align]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const handlePointerDown = (event) => {
      const inButton = rootRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inButton && !inMenu) close(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('touchstart', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('touchstart', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, close, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector('[role="menuitem"]:not(:disabled)');
    first?.focus?.();
  }, [open]);

  if (visibleItems.length === 0) return null;

  const handleMenuKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') return;
    const nodes = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]:not(:disabled)') || []);
    if (nodes.length === 0) return;
    event.preventDefault();
    const current = nodes.indexOf(document.activeElement);
    let next = 0;
    if (event.key === 'ArrowDown') next = current < 0 ? 0 : (current + 1) % nodes.length;
    else if (event.key === 'ArrowUp') next = current <= 0 ? nodes.length - 1 : current - 1;
    else if (event.key === 'End') next = nodes.length - 1;
    nodes[next].focus();
  };

  return (
    <div
      ref={rootRef}
      className={`sg-more${size === 'pill' ? ' sg-more--pill' : ''}${className ? ` ${className}` : ''}`}
    >
      <button
        ref={buttonRef}
        type="button"
        className="sg-more-btn"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          className="sg-more-menu"
          style={position}
          onKeyDown={handleMenuKeyDown}
          onClick={(event) => event.stopPropagation()}
        >
          {visibleItems.map((item) => (
            <button
              key={item.key || item.label}
              type="button"
              role="menuitem"
              className={`sg-more-item${item.tone === 'danger' ? ' sg-more-item--danger' : ''}`}
              disabled={Boolean(item.disabled)}
              onClick={(event) => {
                event.stopPropagation();
                close(false);
                item.onSelect?.(event);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default SaenggibuResultMoreMenu;
